import React, { useState, useEffect } from "react";
import { Box, Typography, SxProps, Theme } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import theme from "@/theme";

interface CommonCheckBoxProps {
    checked?: boolean;
    defaultChecked?: boolean;
    onChange?: (checked: boolean) => void;
    disabled?: boolean;
    label?: string;
    sx?: SxProps<Theme>;
}

export const CommonCheckBox: React.FC<CommonCheckBoxProps> = ({
    checked: controlled,
    defaultChecked = false,
    onChange,
    disabled = false,
    label,
    sx = {},
    ...props
}) => {
    const [internal, setInternal] = useState(defaultChecked);
    const isControlled = controlled !== undefined;
    const isChecked = isControlled ? controlled : internal;

    useEffect(() => { if (isControlled) setInternal(controlled!); }, [controlled, isControlled]);

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (disabled) return;
        const newVal = !isChecked;
        if (!isControlled) setInternal(newVal);
        onChange?.(newVal);
    };

    const node = (
        <Box
            onClick={handleClick}
            sx={{
                width: 20, height: 20, borderRadius: "4px",
                border: `2px solid ${isChecked ? theme.palette.primary.main : theme.palette.border.dark}`,
                bgcolor: isChecked ? theme.palette.primary.main : "transparent",
                display: "flex", alignItems: "center", justifyContent: "center",
                cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
                transition: "all 0.2s ease-in-out", userSelect: "none",
                "&:active": { transform: disabled ? "none" : "scale(0.95)" },
                ...sx,
            }}
            {...props}
        >
            {isChecked && <CheckIcon sx={{ fontSize: 16, color: "custom.white", fontWeight: "bold", stroke: theme.palette.custom.white, strokeWidth: 1 }} />}
        </Box>
    );

    return label ? (
        <Box display="flex" alignItems="center">
            {node}
            <Typography
                fontSize="14px"
                color="text.secondary"
                sx={{
                    ml: "8px",
                    userSelect: "none",
                    WebkitUserSelect: "none",
                    msUserSelect: "none",
                }}
            >
                {label}
            </Typography>
        </Box>
    ) : node;
};